import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../supabaseClient';
import { Loader2, Heart, ChevronDown, SlidersHorizontal, Info, FolderSearch } from 'lucide-react';
import ModernSelect from '../components/ModernSelect';
import CompanyCard from '../components/CompanyCard';
import CompanyInsightsModal from '../components/CompanyInsightsModal';
import ArchiveSearchModal from '../components/ArchiveSearchModal';
import PremiumModal from '../components/PremiumModal';
import { useAuth } from '../context/AuthContext';

const CompaniesPage = () => {
    const { user } = useAuth();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isPremium, setIsPremium] = useState(false);
    const [selectedCompanyForModal, setSelectedCompanyForModal] = useState(null);
    const [showInsightsModal, setShowInsightsModal] = useState(false);
    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);

    const handleArchiveClick = () => {
        if (!user) {
            // Not logged in - could redirect to login
            return;
        }
        if (!isPremium) {
            setShowPremiumModal(true);
            return;
        }
        setShowArchiveModal(true);
    };

    useEffect(() => {
        const checkPremium = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
                const { data } = await supabase.from('profiles').select('is_premium').eq('id', session.user.id).single();
                setIsPremium(data?.is_premium || false);
            }
        };
        checkPremium();
    }, []);

    // Favorites State
    const [favorites, setFavorites] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('favorite_companies') || '[]');
        } catch {
            return [];
        }
    });

    // Filters State
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [selectedSector, setSelectedSector] = useState('');
    const [sortOrder, setSortOrder] = useState('count_desc');

    // Dropdown Data
    const [countries, setCountries] = useState([]);
    const [sectors, setSectors] = useState([]);
    const [cities, setCities] = useState([]);

    // Mobile Filter Dropdowns
    const [openDropdown, setOpenDropdown] = useState(null);

    // CV Matching State
    const [isCvMatchActive, setIsCvMatchActive] = useState(false);
    const [showCvMatchingInfo, setShowCvMatchingInfo] = useState(false);

    // 1. Initial Load: Fetch Filters & Initial Grid
    useEffect(() => {
        const initPage = async () => {
            setLoading(true);
            try {
                const { data: filters, error: filterError } = await supabase.rpc('get_directory_filters');
                if (filterError) throw filterError;

                if (filters && filters.length > 0) {
                    setCountries(filters[0].countries || []);
                    setSectors(filters[0].sectors || []);
                }
                await fetchCompaniesGrid();
            } catch (err) {
                console.error('Error initializing page:', err);
            } finally {
                setLoading(false);
            }
        };
        initPage();
    }, []);

    // 2. Fetch Cities when Country Logic
    useEffect(() => {
        const fetchCities = async () => {
            setSelectedCity('');
            if (!selectedCountry) {
                setCities([]);
                return;
            }
            try {
                const { data, error } = await supabase.rpc('get_cities_for_country', { p_country: selectedCountry });
                if (error) throw error;
                setCities(data || []);
            } catch (err) {
                console.error('Error fetching cities:', err);
            }
        };
        fetchCities();
    }, [selectedCountry]);

    // 3. Fetch Grid Data when Filters Change
    const fetchCompaniesGrid = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase.rpc('get_companies_stats', {
                p_country: selectedCountry || null,
                p_city: selectedCity || null,
                p_sector: selectedSector || null
            });
            if (error) throw error;
            setCompanies(data || []);
        } catch (err) {
            console.error('Error fetching grid:', err);
        } finally {
            setLoading(false);
        }
    };

    // Trigger fetch on filter change
    useEffect(() => {
        fetchCompaniesGrid();
    }, [selectedCountry, selectedCity, selectedSector]);

    // 4. Sorting Logic (Client-Side)
    const sortedCompanies = useMemo(() => {
        if (!companies) return [];
        return [...companies].sort((a, b) => {
            switch (sortOrder) {
                case 'name_asc': return a.name.localeCompare(b.name);
                case 'name_desc': return b.name.localeCompare(a.name);
                case 'count_desc': return b.active - a.active || b.total - a.total;
                case 'count_asc': return a.active - b.active || a.total - b.total;
                default: return b.active - a.active;
            }
        });
    }, [companies, sortOrder]);

    const toggleFavorite = (name) => {
        const newFavs = favorites.includes(name)
            ? favorites.filter(n => n !== name)
            : [...favorites, name];
        setFavorites(newFavs);
        localStorage.setItem('favorite_companies', JSON.stringify(newFavs));
    };

    // Dropdown Options
    const countryOptions = useMemo(() => [
        { value: '', label: 'Tous pays' },
        ...countries.map(c => ({ value: c, label: c }))
    ], [countries]);

    const cityOptions = useMemo(() => [
        { value: '', label: 'Toutes villes' },
        ...cities.map(c => ({ value: c, label: c }))
    ], [cities]);

    const sectorOptions = useMemo(() => [
        { value: '', label: 'Tous secteurs' },
        ...sectors.map(s => ({ value: s, label: s }))
    ], [sectors]);

    const sortOptions = [
        { value: 'count_desc', label: "Plus d'offres actives" },
        { value: 'count_asc', label: "Moins d'offres actives" },
        { value: 'name_asc', label: 'Nom (A-Z)' },
        { value: 'name_desc', label: 'Nom (Z-A)' },
    ];

    const getContextLabel = () => {
        if (selectedCity && selectedCountry) return `${selectedCity}, ${selectedCountry}`;
        if (selectedCountry) return selectedCountry;
        if (selectedSector) return `Secteur ${selectedSector}`;
        return 'GLOBAL';
    };

    // Mobile Dropdown Toggle
    const toggleDropdown = (name) => {
        setOpenDropdown(openDropdown === name ? null : name);
    };

    // Generate avatar colors based on company name (transparent bg, colored text)
    const getAvatarStyle = (name) => {
        const colors = [
            { bg: 'bg-blue-100', text: 'text-blue-600' },
            { bg: 'bg-purple-100', text: 'text-purple-600' },
            { bg: 'bg-green-100', text: 'text-green-600' },
            { bg: 'bg-orange-100', text: 'text-orange-600' },
            { bg: 'bg-pink-100', text: 'text-pink-600' },
            { bg: 'bg-indigo-100', text: 'text-indigo-600' },
            { bg: 'bg-teal-100', text: 'text-teal-600' },
            { bg: 'bg-red-100', text: 'text-red-600' }
        ];
        if (!name) return colors[0];
        const index = name.charCodeAt(0) % colors.length;
        return colors[index];
    };

    return (
        <div className="min-h-screen bg-[#F0F9FF]">
            <Helmet>
                <title>Entreprises qui recrutent en VIE - Plus de 800 sociétés françaises | Trouve ton VIE</title>
                <meta name="description" content="Découvre toutes les entreprises françaises qui recrutent des VIE : grands groupes, ETI, PME. Filtre par secteur, taille, pays d'expatriation." />
                <link rel="canonical" href="https://trouvetonvie.fr/les-entreprises" />
                <meta property="og:title" content="Entreprises qui recrutent en VIE - Plus de 800 sociétés françaises | Trouve ton VIE" />
                <meta property="og:description" content="Découvre toutes les entreprises françaises qui recrutent des VIE : grands groupes, ETI, PME. Filtre par secteur, taille, pays d'expatriation." />
                <meta property="og:url" content="https://trouvetonvie.fr/les-entreprises" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://trouvetonvie.fr/og-image.png" />
                <meta property="og:locale" content="fr_FR" />
                <meta property="og:site_name" content="Trouve ton VIE" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Entreprises qui recrutent en VIE - Plus de 800 sociétés françaises | Trouve ton VIE" />
                <meta name="twitter:description" content="Découvre toutes les entreprises françaises qui recrutent des VIE : grands groupes, ETI, PME. Filtre par secteur, taille, pays d'expatriation." />
                <meta name="twitter:image" content="https://trouvetonvie.fr/og-image.png" />
            </Helmet>

            {/* ========== MOBILE LAYOUT ========== */}
            <div className="md:hidden px-4 py-4">

                {/* Premium Banner */}
                <div className="bg-violet-400/15 rounded-2xl p-5 mb-3 relative overflow-visible border border-violet-100/30 shadow-sm">
                    <div className="flex items-start justify-between">
                        <div className="flex-1 pr-2">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="flex items-center justify-center px-1.5 py-0.5 bg-slate-900 rounded-full">
                                    <img
                                        src="/assets/Trouve+white.png"
                                        alt="Trouve+"
                                        className="h-5 w-auto object-contain"
                                    />
                                </div>
                                <span className="text-[10px] font-semibold text-violet-600 uppercase tracking-wide">
                                    Premium
                                </span>
                            </div>
                            <h3 className="text-slate-900 font-bold text-xl leading-tight mb-1">
                                {isPremium ? "Contacte d'anciens VIE" : "Prends de l'avance"}
                            </h3>
                            <p className="text-slate-600 text-[13px] font-medium leading-[1.4] max-w-[90%] text-justify">
                                {isPremium
                                    ? "Retrouve d'anciens VIE dans ton secteur pour les contacter, en apprendre plus sur leur poste et te faire recommander pour d'éventuelles opportunités."
                                    : "Accède aux anciennes offres pour contacter les VIE déjà en poste : apprends-en plus sur leur équipe et fais-toi recommander pour décrocher un VIE."
                                }
                            </p>
                            <button
                                onClick={handleArchiveClick}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-800 transition-colors mt-2"
                            >
                                <FolderSearch size={14} className="text-violet-400" />
                                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-300 to-violet-300">Explorer les archives</span>
                            </button>
                        </div>
                        {/* Larger tilted avatar frame like screenshot */}
                        <div className="w-24 h-24 flex-shrink-0 rounded-2xl overflow-hidden border-2 border-slate-200/40 shadow-md transform rotate-3 -mr-1 mt-6">
                            <img
                                src="/assets/profile_avatar.png"
                                alt="Avatar"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>


                {/* Archive Button - Mobile */}
                <div
                    className="rounded-2xl py-3 px-4 border-2 transition-all cursor-pointer active:scale-[0.98] shadow-sm hover:shadow-md bg-violet-400/10 border-violet-300 mb-3"
                    onClick={handleArchiveClick}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center justify-center px-2 py-1 bg-slate-900 rounded-full">
                            <img
                                src="/assets/Trouve+white.png"
                                alt="Trouve+"
                                className="h-4 w-auto object-contain"
                            />
                        </div>
                        <span className="text-violet-600 font-bold text-base">
                            Explorer les archives
                        </span>
                        <FolderSearch size={18} className="text-violet-500" />
                    </div>
                </div>

                {/* Title Section */}
                <div className="mb-4">
                    <h1 className="text-2xl font-black text-slate-900 tracking-tight leading-none mb-1">
                        Annuaire des Entreprises
                    </h1>
                    <p className="text-slate-500 font-medium text-sm">
                        {companies.length} entreprises recrutent
                    </p>
                </div>

                {/* Mobile Filters */}
                <div className="flex items-center gap-2 mb-5">
                    {/* Country Dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => toggleDropdown('country')}
                            className="flex items-center justify-between gap-1.5 w-36 px-3 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 hover:border-slate-300 transition-colors"
                        >
                            <span className="truncate">{selectedCountry || 'Pays'}</span>
                            <ChevronDown size={14} className={`flex-shrink-0 transition-transform ${openDropdown === 'country' ? 'rotate-180' : ''}`} />
                        </button>
                        {openDropdown === 'country' && (
                            <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-200 z-50 max-h-60 overflow-y-auto">
                                {countryOptions.map(opt => (
                                    <button
                                        key={opt.value}
                                        onClick={() => { setSelectedCountry(opt.value); setOpenDropdown(null); }}
                                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${selectedCountry === opt.value ? 'bg-blue-50 text-blue-600 font-medium' : 'text-slate-700'}`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Sector Dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => toggleDropdown('sector')}
                            className="flex items-center justify-between gap-1.5 w-36 px-3 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 hover:border-slate-300 transition-colors"
                        >
                            <span className="truncate">{selectedSector || 'Secteurs'}</span>
                            <ChevronDown size={14} className={`flex-shrink-0 transition-transform ${openDropdown === 'sector' ? 'rotate-180' : ''}`} />
                        </button>
                        {openDropdown === 'sector' && (
                            <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 z-50 max-h-60 overflow-y-auto">
                                {sectorOptions.map(opt => (
                                    <button
                                        key={opt.value}
                                        onClick={() => { setSelectedSector(opt.value); setOpenDropdown(null); }}
                                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${selectedSector === opt.value ? 'bg-blue-50 text-blue-600 font-medium' : 'text-slate-700'}`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Sort Button */}
                    <div className="relative ml-auto">
                        <button
                            onClick={() => toggleDropdown('sort')}
                            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 hover:border-slate-300 transition-colors"
                        >
                            <SlidersHorizontal size={14} />
                            Trier
                        </button>
                        {openDropdown === 'sort' && (
                            <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-200 z-50">
                                {sortOptions.map(opt => (
                                    <button
                                        key={opt.value}
                                        onClick={() => { setSortOrder(opt.value); setOpenDropdown(null); }}
                                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${sortOrder === opt.value ? 'bg-blue-50 text-blue-600 font-medium' : 'text-slate-700'}`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Mobile Company Cards */}
                {loading && companies.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-4">
                        <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
                        <p className="text-sm font-medium text-slate-400">Chargement...</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {sortedCompanies.map((company, idx) => (
                            <div
                                key={`${company.name}-${idx}`}
                                className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm cursor-pointer hover:shadow-md hover:border-blue-100 transition-all"
                                onClick={() => { setSelectedCompanyForModal(company.name); setShowInsightsModal(true); }}
                            >
                                {/* Top section: Avatar + Name + Heart */}
                                <div className="flex items-start gap-3">
                                    {/* Avatar */}
                                    <div className={`w-10 h-10 rounded-xl ${getAvatarStyle(company.name).bg} flex items-center justify-center flex-shrink-0`}>
                                        <span className={`${getAvatarStyle(company.name).text} font-bold text-sm`}>
                                            {company.name ? company.name.charAt(0).toUpperCase() : '?'}
                                        </span>
                                    </div>

                                    {/* Name & Context */}
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-bold text-slate-900 text-sm leading-tight truncate">
                                            {company.name}
                                        </h3>
                                        <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wide mt-0.5">
                                            {getContextLabel()}
                                        </p>
                                    </div>

                                    {/* Favorite Button */}
                                    <button
                                        onClick={() => toggleFavorite(company.name)}
                                        className="p-2 -mr-1 -mt-1"
                                    >
                                        <Heart
                                            size={20}
                                            className={favorites.includes(company.name)
                                                ? 'text-blue-500 fill-blue-500'
                                                : 'text-slate-300'
                                            }
                                        />
                                    </button>
                                </div>

                                {/* Separator line */}
                                <div className="border-t border-slate-100 my-3" />

                                {/* Bottom section: Offres disponibles + Actifs/Total */}
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-400">Offres disponibles</span>
                                    <div className="flex items-center gap-2">
                                        <div className="flex flex-col items-center">
                                            <span className="text-[10px] text-slate-400 uppercase">Actifs</span>
                                            <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-lg font-bold text-base">{company.active}</span>
                                        </div>
                                        <div className="flex flex-col items-center">
                                            <span className="text-[10px] text-slate-400 uppercase">Total</span>
                                            <span className="px-3 py-1 bg-violet-400/10 text-violet-600 rounded-lg font-bold text-base">{company.total}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {!loading && sortedCompanies.length === 0 && (
                            <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-gray-200">
                                <p className="text-slate-400 font-medium">Aucune entreprise trouvée.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ========== DESKTOP LAYOUT ========== */}
            <div className="hidden md:block">
                {/* Banner */}
                <div className="max-w-7xl mx-auto px-4 pt-8 pb-4">
                    <div className="bg-violet-400/15 border border-violet-100/30 rounded-xl p-6 flex items-center gap-8 shadow-sm relative overflow-hidden">
                        {/* Tilted Avatar Frame */}
                        <div className="flex-shrink-0 w-32 h-32 relative z-10 transform rotate-3">
                            <div className="w-full h-full rounded-2xl overflow-hidden border-4 border-white/50 shadow-md">
                                <img src="/assets/profile_avatar.png" alt="Avatar" className="w-full h-full object-cover" />
                            </div>
                        </div>

                        <div className="flex-grow z-10 flex flex-col py-1 h-full justify-center">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="flex items-center justify-center px-4 py-2 bg-slate-900 rounded-full">
                                    <img
                                        src="/assets/Trouve+white.png"
                                        alt="Trouve+"
                                        className="h-7 w-auto object-contain"
                                    />
                                </div>
                                <span className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600 uppercase tracking-wide">
                                    {isPremium ? "Vous êtes premium !" : "Premium"}
                                </span>
                            </div>

                            <h2 className="text-2xl font-black text-slate-900 tracking-tight leading-none mb-2">
                                {isPremium ? "Contacte d'anciens VIE" : "Prends de l'avance : contacte des VIE déjà en poste"}
                            </h2>

                            <p className="text-slate-600 text-sm font-medium leading-relaxed max-w-4xl">
                                {isPremium ? (
                                    "Retrouve d'anciens VIE dans ton secteur pour les contacter, en apprendre plus sur leur poste et te faire recommander pour d'éventuelles opportunités dans leur équipe."
                                ) : (
                                    <>
                                        7 VIE sur 10 se décrochent sans passer par une offre en ligne. Accède aux anciennes offres pour contacter les VIE déjà en poste, en apprendre plus sur leur équipe et te faire recommander.
                                        Avec <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600">Trouve+</span>, découvre qui recrute en VIE dans ton pays de choix.
                                    </>
                                )}
                            </p>

                            <div className="mt-4">
                                <button
                                    onClick={handleArchiveClick}
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2 bg-slate-900 text-white rounded-lg text-sm font-bold hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/10"
                                >
                                    <FolderSearch className="w-5 h-5 text-violet-400" />
                                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-300 to-violet-300">Explorer les archives</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">

                    {/* Header & Controls Container */}
                    <div className="flex flex-col xl:flex-row justify-between items-start xl:items-end gap-6 mb-6">
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0">
                                <h1 className="text-3xl font-black text-slate-900 tracking-tight leading-none mb-1">Annuaire des Entreprises</h1>
                                <p className="text-slate-500 font-medium text-sm">
                                    {companies.length} entreprises recrutent {selectedCity ? `à ${selectedCity}, ${selectedCountry}` : (selectedCountry ? `en ${selectedCountry}` : '')}
                                </p>
                            </div>

                            {/* Archive Button - Desktop */}
                            <button
                                onClick={handleArchiveClick}
                                className="group relative flex items-center gap-3 pl-1.5 pr-5 py-1.5 rounded-full border-2 transition-all shadow-sm hover:shadow-md bg-white border-violet-100 hover:border-violet-300"
                            >
                                <div className="flex items-center justify-center bg-slate-900 rounded-full px-2.5 py-1.5 shrink-0 shadow-sm">
                                    <img
                                        src="/assets/Trouve+white.png"
                                        alt="Trouve+"
                                        className="h-5 w-auto object-contain"
                                    />
                                </div>
                                <span className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600">
                                    Explorer les archives
                                </span>
                                <FolderSearch size={16} className="text-violet-500" />
                            </button>
                        </div>

                        {/* Filters */}
                        <div className="flex flex-wrap gap-3 items-center w-full xl:w-auto xl:flex-1 xl:justify-end">
                            <ModernSelect
                                value={selectedCountry}
                                options={countryOptions}
                                onChange={setSelectedCountry}
                                placeholder="Pays"
                                className="min-w-[140px] flex-1"
                            />
                            {selectedCountry && (
                                <ModernSelect
                                    value={selectedCity}
                                    options={cityOptions}
                                    onChange={setSelectedCity}
                                    placeholder="Ville"
                                    className="min-w-[140px] flex-1 animate-in fade-in zoom-in duration-200"
                                />
                            )}
                            <ModernSelect
                                value={selectedSector}
                                options={sectorOptions}
                                onChange={setSelectedSector}
                                placeholder="Secteur"
                                className="min-w-[160px] flex-1"
                            />
                            <ModernSelect
                                value={sortOrder}
                                options={sortOptions}
                                onChange={setSortOrder}
                                placeholder="Trier"
                                className="min-w-[160px] flex-1"
                            />
                        </div>
                    </div>

                    {/* Companies Grid */}
                    {loading && companies.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-4">
                            <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
                            <p className="text-sm font-medium text-slate-400">Chargement de l'annuaire...</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {sortedCompanies.map((company, idx) => (
                                <CompanyCard
                                    key={`${company.name}-${idx}`}
                                    company={company}
                                    isFavorite={favorites.includes(company.name)}
                                    onToggleFavorite={toggleFavorite}
                                    contextLabel={getContextLabel()}
                                    avatarStyle={getAvatarStyle(company.name)}
                                    onClick={(c) => { setSelectedCompanyForModal(c.name); setShowInsightsModal(true); }}
                                />
                            ))}

                            {!loading && sortedCompanies.length === 0 && (
                                <div className="col-span-full text-center py-12 bg-white rounded-2xl border border-dashed border-gray-200">
                                    <p className="text-slate-400 font-medium">Aucune entreprise trouvée pour ces critères.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {/* Company Insights Modal */}
            <CompanyInsightsModal
                companyName={selectedCompanyForModal}
                isOpen={showInsightsModal}
                onClose={() => setShowInsightsModal(false)}
                isPremium={isPremium}
            />

            {/* Archive Search Modal */}
            <ArchiveSearchModal
                isOpen={showArchiveModal}
                onClose={() => setShowArchiveModal(false)}
            />

            {/* Premium Upsell Modal */}
            <PremiumModal
                isOpen={showPremiumModal}
                onClose={() => setShowPremiumModal(false)}
            />
        </div>
    );
};

export default CompaniesPage;
